/**
 * Pipeline warm-up in the real render contexts (M3 plan R20).
 *
 * `renderer.compileAsync(object, camera, scene)` compiles for whatever render target, MRT, context node and override
 * material are current when it is called. From a feature's `init` or an enter hook that is the canvas, but the main
 * scene is drawn by the post pipeline's passes (the HDR MSAA scene pass with the AO context, the AO prepass, the
 * shadow maps). Those pipelines never match, so the first real draw of a new material built its pipeline
 * synchronously: a 0.3–3.4 s freeze per new material set on a first run (measured headless in R20: the first close
 * look at a relay, the first raider in view).
 *
 * `warmPipelines` queues a detached holder and compiles it from inside `scene.onBeforeRender`, which three calls at the
 * start of every render of the scene with that render's target, MRT, context node and override material applied,
 * once per distinct context, while frames render. During the compile every LOD level is visible and frustum culling
 * is off (the holder is detached, so nothing on screen changes). A job settles `settleMs` after its first compile
 * (every pass of a frame has rendered by then); when no frame renders within `fallbackMs` (before the loop starts) it
 * compiles once in the current context instead. Rare events only: it allocates, and the hook costs one branch per
 * render of the scene while nothing is queued.
 */
import { LOD, type Camera, type Object3D, type Scene, type WebGPURenderer } from 'three/webgpu'
import { z } from 'zod'
import { logger } from '../../core/logger'
import warmupJson from './warmup.json'

const warmupSchema = z.object({
  /** How long a job keeps compiling in new contexts after its first compile (ms). */
  settleMs: z.number().positive(),
  /** With no rendered frame within this long, compile once in the current context (ms). */
  fallbackMs: z.number().positive(),
})
export type WarmupConfig = z.infer<typeof warmupSchema>

export function loadWarmupConfig(raw: unknown = warmupJson): WarmupConfig {
  return warmupSchema.parse(raw)
}

/** The renderer surface the warm-up uses (a `WebGPURenderer` satisfies it). */
export interface WarmupRenderer {
  compileAsync(scene: Object3D, camera: Camera, targetScene?: Scene | null): Promise<void>
  getRenderTarget(): { id: number } | null
  getMRT(): { id: number } | null
  readonly contextNode: { id: number } | null
  /** three's node frame (`renderer.inspector.nodeFrame`), whose `frameId` a compile must leave alone (`compileOnce`). */
  readonly inspector?: { readonly nodeFrame: { frameId: number } }
}

/** Timers (tests replace them). */
export interface WarmupClock {
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
}

const defaultClock: WarmupClock = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>),
}

interface Job {
  holder: Object3D
  seen: Set<string>
  promises: Promise<void>[]
  settle: unknown
  fallback: unknown
  restore: () => void
  /** Starts the settle timer (on the first compile). */
  arm: () => void
  done: () => void
}

type BeforeRender = (this: Scene, ...args: unknown[]) => void

interface Warmer {
  jobs: Job[]
  busy: boolean
  renderer: WarmupRenderer
}

const warmers = new WeakMap<Scene, Warmer>()

/** Warm-up jobs queued and not yet resolved, across every scene (`warmupsPending`). */
let pending = 0

/**
 * Warm-up jobs whose compiles have not all finished yet. three r186's `compileAsync` builds a holder's objects one by one
 * with a yield between them, so a big holder (the boot stand-ins) keeps building pipelines for seconds after a small one
 * (the boot plumes) has resolved: a test that waits for one job's log can start flying while another still compiles,
 * and that compile lands in its first-second pipeline count (PERF W1 fix, D-TBD). Waiting for 0 waits for them all.
 */
export function warmupsPending(): number {
  return pending
}

/** Every LOD level visible and frustum culling off under `holder`; returns the undo. */
export function exposeAll(holder: Object3D): () => void {
  const undo: (() => void)[] = []
  holder.traverse((o) => {
    const culled = o.frustumCulled
    o.frustumCulled = false
    undo.push(() => {
      o.frustumCulled = culled
    })
    if (o instanceof LOD) {
      const auto = o.autoUpdate
      o.autoUpdate = false
      undo.push(() => {
        o.autoUpdate = auto
      })
      for (const level of o.levels) {
        const shown = level.object.visible
        level.object.visible = true
        undo.push(() => {
          level.object.visible = shown
        })
      }
    }
  })
  return (): void => {
    for (let i = undo.length - 1; i >= 0; i--) undo[i]()
  }
}

function idOf(v: { id: number } | null | undefined): number {
  return v?.id ?? -1
}

function install(scene: Scene, renderer: WarmupRenderer): Warmer {
  const found = warmers.get(scene)
  if (found) {
    found.renderer = renderer
    return found
  }
  const w: Warmer = { jobs: [], busy: false, renderer }
  const previous = scene.onBeforeRender as unknown as BeforeRender
  const hook: BeforeRender = function (this: Scene, ...args: unknown[]): void {
    if (!w.busy && w.jobs.length > 0) compileHere(w, scene, args[2] as Camera)
    previous.apply(this, args)
  }
  scene.onBeforeRender = hook as unknown as Scene['onBeforeRender']
  warmers.set(scene, w)
  return w
}

/** The renderer's node frame; null before the renderer has initialised (three's getter throws then). */
function nodeFrameOf(r: WarmupRenderer): { frameId: number } | null {
  try {
    return r.inspector?.nodeFrame ?? null
  } catch {
    return null
  }
}

/**
 * three's `compileAsync` calls `nodeFrame.update()` in its synchronous part, advancing `frameId` by one per call.
 * From inside a render (this hook runs in every render of the scene, including the shadow-map renders nested in the
 * HDR scene pass) a dozen queued jobs made one frame look a dozen frames long: `RenderLists.update` then cleared the
 * outer scene pass's list as stale (unused for over 10 frames) while three was still drawing it, and the next draw
 * read an undefined item (the gate-A landing crash, D-TBD). A compile is not a frame, so the frame id is put back
 * once the synchronous part returns.
 */
function compileOnce(w: Warmer, job: Job, camera: Camera, scene: Scene): void {
  w.busy = true
  const frame = nodeFrameOf(w.renderer)
  const frameId = frame?.frameId ?? 0
  try {
    job.promises.push(
      w.renderer.compileAsync(job.holder, camera, scene).catch((err: unknown) => {
        logger.warn('reach warm-up: a compile failed; those pipelines build on first draw', err)
      }),
    )
  } finally {
    if (frame !== null) frame.frameId = frameId
    w.busy = false
  }
  job.arm()
}

function compileHere(w: Warmer, scene: Scene, camera: Camera): void {
  const r = w.renderer
  const key = `${idOf(r.getRenderTarget())}:${idOf(r.getMRT())}:${idOf(r.contextNode)}:${scene.overrideMaterial?.uuid ?? "-"}:${camera.id}`
  for (let i = 0; i < w.jobs.length; i++) {
    const job = w.jobs[i]
    if (job.seen.has(key)) continue
    job.seen.add(key)
    compileOnce(w, job, camera, scene)
  }
}

function finish(w: Warmer, clock: WarmupClock, job: Job): void {
  const i = w.jobs.indexOf(job)
  if (i < 0) return
  w.jobs.splice(i, 1)
  clock.clearTimeout(job.settle)
  clock.clearTimeout(job.fallback)
  job.done()
}

/**
 * Compiles `holder` (detached from the scene) in every context the scene renders in over the next frames. Resolves
 * with the number of contexts it compiled in (0: the fallback compiled in the current one) once the compiles have
 * finished; never rejects.
 */
export function warmPipelines(
  renderer: WebGPURenderer | WarmupRenderer,
  scene: Scene,
  camera: Camera,
  holder: Object3D,
  cfg: WarmupConfig = loadWarmupConfig(),
  clock: WarmupClock = defaultClock,
): Promise<number> {
  const w = install(scene, renderer as WarmupRenderer)
  pending++
  return new Promise<number>((resolve) => {
    const job: Job = {
      holder,
      seen: new Set(),
      promises: [],
      settle: null,
      fallback: null,
      restore: exposeAll(holder),
      arm: (): void => {
        if (job.settle === null) job.settle = clock.setTimeout(() => finish(w, clock, job), cfg.settleMs)
      },
      done: (): void => {
        void Promise.all(job.promises).then(() => {
          job.restore()
          pending--
          resolve(job.seen.size)
        })
      },
    }
    job.fallback = clock.setTimeout(() => {
      if (job.seen.size === 0) compileOnce(w, job, camera, scene)
      finish(w, clock, job)
    }, cfg.fallbackMs)
    w.jobs.push(job)
  })
}
